import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Inbox, ExternalLink } from 'lucide-react';
import { notificationsApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

const RED = '#ff5c5c';
const BLUE = '#4a9eff';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: unread } = useQuery({
    queryKey: ['notif-unread'],
    queryFn: () => notificationsApi.unreadCount().then((r) => r.data.count),
    refetchInterval: 30_000, retry: false,
  });
  const { data: inbox = [] } = useQuery({
    queryKey: ['notif-inbox'],
    queryFn: () => notificationsApi.inbox(15).then((r) => r.data),
    enabled: open, retry: false,
  });
  const markRead = useMutation({
    mutationFn: (id) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-unread'] });
      qc.invalidateQueries({ queryKey: ['notif-inbox'] });
    },
  });

  useEffect(() => {
    const closer = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', closer);
    return () => document.removeEventListener('mousedown', closer);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} title="Notifications"
        style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
          cursor: 'pointer', padding: 6, borderRadius: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
        }}>
        <Inbox size={18} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 0, minWidth: 16, height: 16, borderRadius: 8,
            background: BLUE, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1,
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 40, right: 0, width: 380, maxHeight: 480, overflow: 'auto',
          background: '#0d1527', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)', zIndex: 100,
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Inbox</div>
            <button onClick={() => { setOpen(false); navigate('/notifications-settings'); }}
              style={{ background: 'none', border: 'none', color: BLUE, fontSize: 11, cursor: 'pointer' }}>
              Manage subscriptions
            </button>
          </div>

          {inbox.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
              No notifications yet.
            </div>
          ) : inbox.map((n) => {
            const unreadRow = !n.readAt;
            return (
              <div key={n.id} onClick={() => {
                if (unreadRow) markRead.mutate(n.id);
                if (n.linkUrl) { setOpen(false); navigate(n.linkUrl); }
              }}
                style={{
                  padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)',
                  cursor: 'pointer', background: unreadRow ? 'rgba(74,158,255,0.04)' : 'transparent',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                onMouseLeave={(e) => e.currentTarget.style.background = unreadRow ? 'rgba(74,158,255,0.04)' : 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {unreadRow && (
                    <div style={{ width: 6, height: 6, borderRadius: 3, background: BLUE, marginTop: 6, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{n.subject}</div>
                    {n.body && (
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 3,
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden' }}>
                        {n.body}
                      </div>
                    )}
                    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, marginTop: 4, display: 'flex', gap: 8 }}>
                      <span>{n.eventKind}</span>
                      {n.linkUrl && <ExternalLink size={10} />}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
